import { useEffect, useState, type ChangeEvent, type ReactElement } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  NOTIFICATION_EMAIL_CATEGORIES,
  NOTIFICATION_MOBILE_PUSH_CATEGORIES,
} from '@molt/shared';
import { Notice, Skeleton } from '@/components';
import {
  desktopNotificationPermission,
  mobilePushInstallationsQueryOptions,
  notifyDesktopNotificationSettingChange,
  notificationPreferencesQueryOptions,
  readDesktopNotificationSetting,
  writeDesktopNotificationSetting,
  type DesktopNotificationPermission,
  useDeleteMobilePushInstallation,
  useSetNotificationEmailCategory,
  useSetNotificationEmailFrequency,
  useSetNotificationMobilePushCategory,
} from '@/lib/notifications';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { queryKeys } from '@/lib/query-keys';
import {
  currentWebPushSubscription,
  disableWebPush,
  enableWebPush,
  webPushConfigQueryOptions,
  webPushPermission,
  webPushRegistration,
  webPushSupported,
  type WebPushPermission,
} from '@/lib/web-push';

export interface NotificationPreferencesPanelProps {
  readonly open: boolean;
}

export function NotificationPreferencesPanel({
  open,
}: NotificationPreferencesPanelProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const preferences = useQuery({
    ...notificationPreferencesQueryOptions(),
    enabled: open,
  });
  const installations = useQuery({
    ...mobilePushInstallationsQueryOptions(),
    enabled: open,
  });
  const setFrequency = useSetNotificationEmailFrequency();
  const setCategory = useSetNotificationEmailCategory();
  const setPushCategory = useSetNotificationMobilePushCategory();
  const deleteInstallation = useDeleteMobilePushInstallation();
  const [desktopEnabled, setDesktopEnabled] = useState(false);
  const [desktopPermission, setDesktopPermission] =
    useState<DesktopNotificationPermission>('unsupported');

  useEffect(() => {
    if (!open) return;
    setDesktopEnabled(readDesktopNotificationSetting());
    setDesktopPermission(desktopNotificationPermission());
  }, [open]);

  /*
   * Web Push on this device (`lib/web-push.ts`). Offered only where it can
   * work: a browser with a push manager, a `sw.js` registration (https, or
   * the `VITE_ENABLE_SW` opt-in) and a VAPID key on the server. The config
   * query is gated on support, so a browser without it never asks.
   */
  const queryClient = useQueryClient();
  const pushSupported = open && webPushSupported();
  const pushConfig = useQuery({
    ...webPushConfigQueryOptions(),
    enabled: pushSupported,
  });
  const [pushRegistered, setPushRegistered] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushPermission, setPushPermission] = useState<WebPushPermission>('unsupported');
  const [pushBusy, setPushBusy] = useState(false);
  const [pushNote, setPushNote] = useState<'dismissed' | 'failed' | null>(null);

  useEffect(() => {
    if (!pushSupported) return undefined;
    let live = true;
    setPushPermission(webPushPermission());
    void (async () => {
      const registration = await webPushRegistration();
      const subscription =
        registration === null ? null : await currentWebPushSubscription();
      if (!live) return;
      setPushRegistered(registration !== null);
      setPushSubscribed(subscription !== null && webPushPermission() === 'granted');
    })().catch(() => {
      // An unreadable push manager leaves the control hidden.
    });
    return () => {
      live = false;
    };
  }, [pushSupported]);

  if (!open) return <></>;

  const pushOffered =
    pushSupported && pushRegistered && typeof pushConfig.data?.publicKey === 'string';
  const pushBlocked = pushPermission === 'denied';

  async function handlePushChange(event: ChangeEvent<HTMLInputElement>) {
    const next = event.currentTarget.checked;
    setPushBusy(true);
    setPushNote(null);
    try {
      if (next) {
        // `enableWebPush` asks for permission before anything else awaits.
        const result = await enableWebPush();
        setPushPermission(webPushPermission());
        setPushSubscribed(result === 'enabled');
        if (result === 'dismissed') setPushNote('dismissed');
        else if (result !== 'enabled' && result !== 'denied') setPushNote('failed');
      } else {
        await disableWebPush();
        setPushSubscribed(false);
      }
    } catch {
      setPushNote('failed');
      setPushSubscribed((await currentWebPushSubscription().catch(() => null)) !== null);
    } finally {
      setPushBusy(false);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.mobilePushInstallations(),
      });
    }
  }

  const data = preferences.data;
  const installationData = installations.data;
  const blocked = desktopPermission === 'denied';
  const unsupported = desktopPermission === 'unsupported';
  const desktopChecked =
    desktopEnabled && desktopPermission === 'granted' && !unsupported && !blocked;
  const mobileBlocked =
    installationData?.installations.some(
      (installation) => installation.permissionStatus === 'denied',
    ) ?? false;

  async function handleDesktopChange(event: ChangeEvent<HTMLInputElement>) {
    const next = event.currentTarget.checked;
    if (!next) {
      writeDesktopNotificationSetting(false);
      setDesktopEnabled(false);
      notifyDesktopNotificationSettingChange();
      return;
    }

    const permission = desktopNotificationPermission();
    if (permission === 'unsupported' || permission === 'denied') {
      writeDesktopNotificationSetting(false);
      setDesktopEnabled(false);
      setDesktopPermission(permission);
      notifyDesktopNotificationSettingChange();
      return;
    }

    const nextPermission =
      permission === 'default'
        ? await window.Notification.requestPermission()
        : permission;
    setDesktopPermission(nextPermission);
    const granted = nextPermission === 'granted';
    writeDesktopNotificationSetting(granted);
    setDesktopEnabled(granted);
    notifyDesktopNotificationSettingChange();
  }

  /*
   * The drawer's body is `overflow-hidden` and its one scroll region is the
   * history list below this panel, so on a short (phone) viewport the panel was
   * clipped with no scrollable ancestor and a touch scroll chained through to
   * the board behind. It scrolls itself, shrinking before the history does, and
   * `overscroll-contain` keeps the scroll from chaining at either end.
   */
  return (
    <section
      aria-labelledby="notification-settings-heading"
      className="min-h-0 shrink space-y-4 overflow-y-auto overscroll-contain border-b border-border px-3 py-3"
    >
      <h3 id="notification-settings-heading" className="text-sm font-semibold text-ink">
        {t('notifications.settings')}
      </h3>

      {preferences.isLoading || data === undefined ? (
        <div aria-busy="true" aria-label={t('notifications.preferences.loading')}>
          <Skeleton shape="row" count={4} />
        </div>
      ) : preferences.isError ? (
        <Notice
          tone="bad"
          title={t('notifications.preferences.loadFailedTitle')}
          body={t('notifications.tryOpenAgain')}
        />
      ) : (
        <>
          <fieldset className="space-y-2">
            <legend className="text-xs font-medium uppercase text-ink-muted">
              {t('notifications.preferences.emailFrequency')}
            </legend>
            <div className="grid grid-cols-3 gap-1">
              {(['instantly', 'periodically', 'never'] as const).map((frequency) => (
                <label
                  key={frequency}
                  className="flex min-h-8 items-center justify-center rounded-card border border-border-strong bg-surface px-2 text-xs text-ink has-[:checked]:border-accent has-[:checked]:bg-accent has-[:checked]:text-accent-ink has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent"
                >
                  <input
                    type="radio"
                    name="notification-email-frequency"
                    value={frequency}
                    checked={data.emailFrequency === frequency}
                    disabled={setFrequency.isPending}
                    onChange={() => setFrequency.mutate(frequency)}
                    className="sr-only"
                  />
                  <span>{t(`notifications.preferences.frequency.${frequency}`)}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="space-y-2">
            <legend className="text-xs font-medium uppercase text-ink-muted">
              {t('notifications.preferences.emailTypes')}
            </legend>
            <div className="grid gap-1">
              {NOTIFICATION_EMAIL_CATEGORIES.map((category) => {
                const label = t(`notifications.preferences.category.${category}`);
                const name = t(`notifications.preferences.categoryName.${category}`);
                const state = t(
                  data.emailCategories[category]
                    ? 'notifications.preferences.on'
                    : 'notifications.preferences.off',
                );
                const checked = data.emailCategories[category];
                return (
                  <label
                    key={category}
                    className="flex min-h-8 items-center justify-between gap-2 rounded-card bg-surface px-2 text-sm text-ink has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent"
                  >
                    <span>{label}</span>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={setCategory.isPending}
                      aria-label={t('notifications.preferences.emailToggle', {
                        category: name,
                        state,
                      })}
                      onChange={(event) =>
                        setCategory.mutate({
                          category,
                          enabled: event.currentTarget.checked,
                        })
                      }
                      className="size-4 accent-accent"
                    />
                  </label>
                );
              })}
            </div>
          </fieldset>

          <fieldset className="space-y-2">
            <legend className="text-xs font-medium uppercase text-ink-muted">
              {t('notifications.preferences.mobilePushTypes')}
            </legend>
            {mobileBlocked ? (
              <Notice tone="info" body={t('notifications.preferences.mobileBlocked')} />
            ) : null}
            <div className="grid gap-1">
              {NOTIFICATION_MOBILE_PUSH_CATEGORIES.map((category) => {
                const label = t(`notifications.preferences.mobileCategory.${category}`);
                const name = t(
                  `notifications.preferences.mobileCategoryName.${category}`,
                );
                const state = t(
                  data.mobilePushCategories[category]
                    ? 'notifications.preferences.on'
                    : 'notifications.preferences.off',
                );
                const checked = data.mobilePushCategories[category];
                return (
                  <label
                    key={category}
                    className="flex min-h-8 items-center justify-between gap-2 rounded-card bg-surface px-2 text-sm text-ink has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent"
                  >
                    <span>{label}</span>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={setPushCategory.isPending}
                      aria-label={t('notifications.preferences.mobileToggle', {
                        category: name,
                        state,
                      })}
                      onChange={(event) =>
                        setPushCategory.mutate({
                          category,
                          enabled: event.currentTarget.checked,
                        })
                      }
                      className="size-4 accent-accent"
                    />
                  </label>
                );
              })}
            </div>
          </fieldset>
        </>
      )}

      {open &&
      installationData !== undefined &&
      installationData.installations.length > 0 ? (
        <div className="space-y-2 rounded-card bg-surface px-2 py-2">
          <p className="text-xs font-medium uppercase text-ink-muted">
            {t('notifications.preferences.mobileInstalls')}
          </p>
          <ul
            className="grid gap-1"
            aria-label={t('notifications.preferences.mobileInstallList')}
          >
            {installationData.installations.map((installation) => (
              <li
                key={installation.id}
                className="flex items-center justify-between gap-2 text-sm text-ink"
              >
                <span>
                  {t('notifications.preferences.mobileInstallDescription', {
                    platform: t(
                      `notifications.preferences.platform.${installation.platform}`,
                    ),
                    permission: t(
                      `notifications.preferences.permission.${installation.permissionStatus}`,
                    ),
                  })}
                </span>
                <button
                  type="button"
                  className="rounded-card px-2 py-1 text-xs font-medium text-ink-muted underline-offset-2 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                  disabled={deleteInstallation.isPending}
                  onClick={() => deleteInstallation.mutate(installation.id)}
                >
                  {t('notifications.preferences.removeInstall')}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {pushOffered ? (
        <div className="space-y-2 rounded-card bg-surface px-2 py-2">
          <label className="flex min-h-8 items-center justify-between gap-3 text-sm text-ink">
            <span>{t('notifications.preferences.webPush')}</span>
            <input
              type="checkbox"
              checked={pushSubscribed && !pushBlocked}
              disabled={pushBusy || pushBlocked}
              aria-label={t('notifications.preferences.webPushToggle', {
                state: t(
                  pushSubscribed && !pushBlocked
                    ? 'notifications.preferences.on'
                    : 'notifications.preferences.off',
                ),
              })}
              onChange={(event) => {
                void handlePushChange(event);
              }}
              className="size-4 accent-accent"
            />
          </label>
          <p className="text-xs text-ink-muted">
            {t(
              pushBlocked
                ? 'notifications.preferences.webPushBlocked'
                : pushNote === 'dismissed'
                  ? 'notifications.preferences.webPushDismissed'
                  : pushNote === 'failed'
                    ? 'notifications.preferences.webPushFailed'
                    : 'notifications.preferences.webPushClosedApp',
            )}
          </p>
        </div>
      ) : null}

      <div className="space-y-2 rounded-card bg-surface px-2 py-2">
        <label className="flex min-h-8 items-center justify-between gap-3 text-sm text-ink">
          <span>{t('notifications.preferences.desktop')}</span>
          <input
            type="checkbox"
            checked={desktopChecked}
            disabled={unsupported}
            aria-label={t('notifications.preferences.desktopToggle', {
              state: t(
                desktopChecked
                  ? 'notifications.preferences.on'
                  : 'notifications.preferences.off',
              ),
            })}
            onChange={(event) => {
              void handleDesktopChange(event);
            }}
            className="size-4 accent-accent"
          />
        </label>
        {blocked ? (
          <p className="text-xs text-ink-muted">
            {t('notifications.preferences.desktopBlocked')}
          </p>
        ) : unsupported ? (
          <p className="text-xs text-ink-muted">
            {t('notifications.preferences.desktopUnsupported')}
          </p>
        ) : (
          <p className="text-xs text-ink-muted">
            {t('notifications.preferences.desktopOpenOnly')}
          </p>
        )}
      </div>
    </section>
  );
}
